var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var _a, _b, _c, _d, _e;
import { createTheme, getContrastRatio } from '@mui/material';
import { colorPalette } from './color-palette';
var materialTheme = createTheme();
function getDynamicContrastText(background, lightText, darkText) {
    var lightContrast = getContrastRatio(background, lightText);
    var darkContrast = getContrastRatio(background, darkText);
    return lightContrast >= darkContrast ? lightText : darkText;
}
export var theme = {
    typography: {
        htmlFontSize: 16,
        h1: {
            fontSize: 43,
            fontWeight: 500,
        },
        h2: (_a = {
                fontSize: 36,
                fontWeight: 500
            },
            _a[materialTheme.breakpoints.down('md')] = {
                fontSize: 20,
            },
            _a),
        h3: {
            fontSize: 28,
            fontWeight: 700,
        },
        h4: {
            fontSize: 24,
            fontWeight: 500,
            letterSpacing: 0,
        },
        h5: {
            fontSize: 20,
            fontWeight: 500,
        },
        widgetAmount: (_b = {
                fontSize: 52,
                fontWeight: 900,
                lineHeight: '19px'
            },
            _b[materialTheme.breakpoints.down('md')] = {
                fontSize: 38,
                lineHeight: '29px',
            },
            _b[materialTheme.breakpoints.down('sm')] = {
                fontSize: 24,
                lineHeight: '29px',
            },
            _b),
        widgetHeader: (_c = {
                fontSize: 32,
                lineHeight: '29px',
                fontWeight: 600
            },
            _c[materialTheme.breakpoints.down('sm')] = {
                fontSize: 24,
            },
            _c),
        widgetHeaderExtraSmall: {
            fontSize: 28,
            lineHeight: '29px',
            fontWeight: 600,
        },
        widgetHeaderExtraSmallVariant5: {
            fontSize: 22,
            lineHeight: '21px',
            fontWeight: 600,
        },
        textField: (_d = {
                fontSize: 16
            },
            _d[materialTheme.breakpoints.down('md')] = {
                fontSize: 12,
            },
            _d),
        radioButtonLabel: (_e = {
                fontSize: 28,
                fontWeight: 600
            },
            _e[materialTheme.breakpoints.down('xl')] = {
                fontSize: 22,
            },
            _e[materialTheme.breakpoints.down('md')] = {
                fontSize: 16,
            },
            _e),
        ribbon: {
            fontSize: 14,
            fontWeight: 600,
        },
        body1: {
            fontSize: 14,
        },
        body2: {
            fontSize: 14,
            fontWeight: 500,
        },
        subtitle1: {
            fontSize: 12,
        },
        subtitle2: {
            fontSize: 12,
            fontWeight: 500,
        },
        subtitle3: {
            fontSize: 10,
        },
    },
    components: {
        MuiButtonBase: {
            defaultProps: {
                disableRipple: true,
            },
        },
        MuiSlider: {
            styleOverrides: {
                root: {
                    height: 10,
                    '& .MuiInputBase-root': {
                        fontSize: '10px',
                    },
                },
            },
        },
        MuiCssBaseline: {
            styleOverrides: {
                html: {
                    height: '100%',
                },
                body: {
                    height: '100%',
                    '&::-webkit-scrollbar, & *::-webkit-scrollbar': {
                        backgroundColor: colorPalette.white,
                        width: 4,
                    },
                    '&::-webkit-scrollbar-thumb, & *::-webkit-scrollbar-thumb': {
                        borderRadius: 8,
                        backgroundColor: colorPalette.ink.main,
                    },
                    '&::-webkit-scrollbar-thumb:focus, & *::-webkit-scrollbar-thumb:focus': {
                        backgroundColor: colorPalette.ink.main,
                    },
                    '&::-webkit-scrollbar-thumb:active, & *::-webkit-scrollbar-thumb:active': {
                        backgroundColor: colorPalette.ink.main,
                    },
                    '&::-webkit-scrollbar-thumb:hover, & *::-webkit-scrollbar-thumb:hover': {
                        backgroundColor: colorPalette.ink.main,
                    },
                    '&::-webkit-scrollbar-corner, & *::-webkit-scrollbar-corner': {
                        backgroundColor: colorPalette.ink.main,
                    },
                },
                '#root': {
                    height: '100%',
                },
            },
        },
        MuiTypography: {
            defaultProps: {
                variant: 'body1',
                color: colorPalette.ink.main,
                fontFamily: 'Roboto',
                variantMapping: {
                    subtitle1: 'p',
                    subtitle2: 'p',
                    textField: 'p',
                },
            },
        },
        MuiButton: {
            styleOverrides: {
                root: function (_a) {
                    var ownerState = _a.ownerState;
                    return (__assign({ fontSize: '.875rem', borderRadius: '8px', fontWeight: 500, '&.Mui-disabled': {
                            backgroundColor: colorPalette.ink.light,
                            color: colorPalette.white,
                        } }, ((ownerState.color === 'info' || ownerState.color === 'secondary') && {
                        backgroundColor: colorPalette[ownerState.color].light,
                        '&:hover': {
                            backgroundColor: colorPalette[ownerState.color].main,
                        },
                        '&:active': {
                            backgroundColor: colorPalette[ownerState.color].dark,
                        },
                    })));
                },
            },
        },
        MuiCheckbox: {
            styleOverrides: {
                root: {
                    padding: 0,
                    '&.Mui-checked': {
                        color: colorPalette.ink.main,
                    },
                },
            },
        },
    },
    palette: {
        primary: {
            main: colorPalette.primary.main,
            light: colorPalette.primary.light,
            dark: colorPalette.primary.dark,
            contrastText: getDynamicContrastText(colorPalette.primary.main, colorPalette.primary.contrast, colorPalette.primary.contrastDark),
        },
        secondary: {
            main: colorPalette.secondary.main,
            light: colorPalette.secondary.light,
            dark: colorPalette.secondary.dark,
            contrastText: getDynamicContrastText(colorPalette.secondary.main, colorPalette.secondary.contrast, colorPalette.secondary.contrastDark),
        },
        error: {
            main: colorPalette.error.main,
            light: colorPalette.error.light,
            dark: colorPalette.error.dark,
            contrastText: getDynamicContrastText(colorPalette.error.main, colorPalette.error.contrast, colorPalette.error.contrastDark),
        },
        warning: {
            main: colorPalette.warning.main,
            light: colorPalette.warning.light,
            dark: colorPalette.warning.dark,
            contrastText: getDynamicContrastText(colorPalette.warning.main, colorPalette.warning.contrast, colorPalette.warning.contrastDark),
        },
        info: {
            main: colorPalette.info.main,
            light: colorPalette.info.light,
            dark: colorPalette.info.dark,
            contrastText: getDynamicContrastText(colorPalette.info.main, colorPalette.info.contrast, colorPalette.info.contrastDark),
        },
        success: {
            main: colorPalette.success.main,
            light: colorPalette.success.light,
            dark: colorPalette.success.dark,
            contrastText: getDynamicContrastText(colorPalette.success.main, colorPalette.success.contrast, colorPalette.success.contrastDark),
        },
        ink: {
            main: colorPalette.ink.main,
            light: colorPalette.ink.light,
            dark: colorPalette.ink.dark,
            contrastText: getDynamicContrastText(colorPalette.ink.main, colorPalette.ink.contrast, colorPalette.ink.contrastDark),
        },
        pale: {
            main: colorPalette.pale.main,
            light: colorPalette.pale.light,
            dark: colorPalette.pale.dark,
            contrastText: getDynamicContrastText(colorPalette.pale.main, colorPalette.pale.contrast, colorPalette.pale.contrastDark),
        },
        cloud: {
            main: colorPalette.cloud.main,
            light: colorPalette.cloud.light,
            dark: colorPalette.cloud.dark,
            contrastText: getDynamicContrastText(colorPalette.cloud.main, colorPalette.cloud.contrast, colorPalette.cloud.contrastDark),
        },
    },
    breakpoints: {
        values: {
            xxs: 0,
            xs: 240,
            sm: 600,
            md: 900,
            lg: 1200,
            xl: 1536,
            xxl: 1800,
        },
    },
};
